import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const SupportTicketsList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statistics, setStatistics] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadTickets();
    loadStatistics();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [statusFilter, priorityFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchTerm) {
        filters.search = searchTerm;
      }
      if (statusFilter) {
        filters.status = statusFilter;
      }
      if (priorityFilter) {
        filters.priority = priorityFilter;
      }

      const response = await api.getSupportTickets(filters);
      if (response.success) {
        setTickets(response.data || []);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.getSupportTicketStatistics();
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadTickets();
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-primary',
      in_progress: 'bg-info',
      pending_customer: 'bg-warning',
      resolved: 'bg-success',
      closed: 'bg-secondary',
    };
    return badges[status] || 'bg-secondary';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: 'bg-secondary',
      medium: 'bg-info',
      high: 'bg-warning',
      urgent: 'bg-danger',
    };
    return badges[priority] || 'bg-secondary';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="row mb-3">
          <div className="col-lg-3 col-6">
            <div className="small-box bg-info">
              <div className="inner">
                <h3>{statistics.total || 0}</h3>
                <p>Total Tickets</p>
              </div>
              <div className="icon">
                <i className="fas fa-ticket-alt"></i>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-6">
            <div className="small-box bg-warning">
              <div className="inner">
                <h3>{statistics.open || 0}</h3>
                <p>Open</p>
              </div>
              <div className="icon">
                <i className="fas fa-folder-open"></i>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-6">
            <div className="small-box bg-success">
              <div className="inner">
                <h3>{statistics.resolved || 0}</h3>
                <p>Resolved</p>
              </div>
              <div className="icon">
                <i className="fas fa-check-circle"></i>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-6">
            <div className="small-box bg-danger">
              <div className="inner">
                <h3>{(statistics.urgent || 0) + (statistics.high || 0)}</h3>
                <p>Urgent/High</p>
              </div>
              <div className="icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Support Tickets</h3>
              <div className="card-tools">
                <form className="form-inline" onSubmit={handleSearch}>
                  <div className="input-group input-group-sm mr-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="input-group-append">
                      <button type="submit" className="btn btn-default">
                        <i className="fas fa-search"></i>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>Status:</label>
                  <select
                    className="form-control form-control-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending_customer">Pending Customer</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label>Priority:</label>
                  <select
                    className="form-control form-control-sm"
                    value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                  >
                    <option value="">All Priorities</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-bordered table-hover table-sm">
                  <thead>
                    <tr>
                      <th>Ticket #</th>
                      <th>Subject</th>
                      <th>Customer</th>
                      <th>License Key</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Assigned To</th>
                      <th>Created</th>
                      <th>Attachments</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.length > 0 ? (
                      tickets.map(ticket => (
                        <tr key={ticket.id}>
                          <td>
                            <code style={{ fontSize: '11px' }}>{ticket.ticket_number}</code>
                          </td>
                          <td>
                            <strong>{ticket.subject}</strong>
                            {ticket.category && (
                              <div>
                                <small className="text-muted">{ticket.category}</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <div>{ticket.customer_name}</div>
                            <small className="text-muted">{ticket.customer_email}</small>
                          </td>
                          <td>
                            <code style={{ fontSize: '10px' }}>{ticket.license_key}</code>
                            {ticket.company_name && (
                              <div>
                                <small className="text-muted">{ticket.company_name}</small>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`badge ${getStatusBadge(ticket.status)}`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${getPriorityBadge(ticket.priority)}`}>
                              {ticket.priority.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            {ticket.assigned_to_name || <span className="text-muted">Unassigned</span>}
                          </td>
                          <td style={{ fontSize: '11px' }}>
                            {formatDate(ticket.created_at)}
                          </td>
                          <td className="text-center">
                            {ticket.attachment_count > 0 && (
                              <span className="badge bg-info">
                                <i className="fas fa-paperclip"></i> {ticket.attachment_count}
                              </span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-info mr-1"
                              onClick={() => navigate(`/support-tickets/${ticket.id}`)}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center">No support tickets found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportTicketsList;

